import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import studentService from '../services/studentService';

const RegistrationApprovalsPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await studentService.getPendingRegistrations();
      console.log('Pending registrations response:', response);
      setRegistrations(response.pending_registrations || []);
    } catch (err) {
      console.error('Error loading registrations:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load pending registrations';
      setError(errorMsg);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (window.confirm('Are you sure you want to approve this registration?')) {
      try {
        await studentService.approveRegistration(id);
        loadRegistrations();
        window.dispatchEvent(new Event('refreshPendingCounts'));
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to approve registration');
      }
    }
  };

  const handleReject = async (id) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      try {
        await studentService.rejectRegistration(id, reason);
        loadRegistrations();
        window.dispatchEvent(new Event('refreshPendingCounts'));
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to reject registration');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  };

  return (
    <Layout title="Registration Approvals">
      <style>{`
        .stats-row { display: flex; gap: 20px; margin-bottom: 24px; }
        .stat-card { background: white; padding: 20px; border-radius: 16px; border: 1px solid rgba(0, 0, 0, 0.04); flex: 1; box-shadow: 0 6px 12px -2px rgba(245, 158, 11, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px -4px rgba(245, 158, 11, 0.25), 0 15px 25px -8px rgba(0, 0, 0, 0.08); }
        .stat-card h3 { font-size: 12px; color: #6b7280; margin: 0 0 8px; text-transform: uppercase; }
        .stat-card .value { font-size: 28px; font-weight: 600; color: #1f2937; }
        .stat-card .value.pending { color: #f59e0b; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        .data-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; font-size: 14px; }
        .data-table tr:hover { background: #f9fafb; }
        .btn { padding: 8px 14px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500; font-size: 13px; }
        .btn-success { background: #d1fae5; color: #047857; }
        .btn-success:hover { background: #a7f3d0; }
        .btn-danger { background: #fee2e2; color: #dc2626; }
        .btn-danger:hover { background: #fecaca; }
        .btn-secondary { background: #f3f4f6; color: #374151; }
        .actions { display: flex; gap: 8px; }
        .status-badge { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; background: #fef3c7; color: #b45309; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal { background: white; border-radius: 16px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 40px -10px rgba(99, 102, 241, 0.25), 0 30px 50px -15px rgba(0, 0, 0, 0.15); border: 1px solid rgba(0, 0, 0, 0.04); }
        .modal-header { padding: 20px 24px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
        .modal-header h2 { margin: 0; font-size: 18px; font-weight: 600; }
        .modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #6b7280; }
        .modal-body { padding: 24px; }
        .modal-footer { padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 12px; }
        .detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
        .detail-item { padding: 12px; background: white; border-radius: 12px; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); border: 1px solid rgba(0, 0, 0, 0.04); }
        .detail-item label { display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px; text-transform: uppercase; }
        .detail-item span { font-weight: 500; color: #1f2937; font-size: 14px; }
        .loading, .error, .empty { text-align: center; padding: 48px; color: #6b7280; }
        .error { color: #dc2626; }
      `}</style>

      <div className="stats-row">
        <div className="stat-card">
          <h3>Pending Registrations</h3>
          <div className="value pending">{registrations.length}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading registrations...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : registrations.length === 0 ? (
        <div className="empty">No pending registrations</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Registered</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations.map((reg) => (
              <tr key={reg.id}>
                <td><strong>{reg.user?.first_name} {reg.user?.last_name}</strong></td>
                <td>{reg.user?.email}</td>
                <td>{reg.user?.phone}</td>
                <td>{formatDate(reg.app_registration_date || reg.user?.created_at)}</td>
                <td><span className="status-badge">Pending</span></td>
                <td>
                  <div className="actions">
                    <button className="btn btn-secondary" onClick={() => { setSelectedRegistration(reg); setShowModal(true); }}>View</button>
                    <button className="btn btn-success" onClick={() => handleApprove(reg.id)}>Approve</button>
                    <button className="btn btn-danger" onClick={() => handleReject(reg.id)}>Reject</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && selectedRegistration && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registration Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item"><label>Full Name</label><span>{selectedRegistration.user?.first_name} {selectedRegistration.user?.last_name}</span></div>
                <div className="detail-item"><label>Email</label><span>{selectedRegistration.user?.email}</span></div>
                <div className="detail-item"><label>Phone</label><span>{selectedRegistration.user?.phone}</span></div>
                <div className="detail-item"><label>Gender</label><span>{selectedRegistration.gender || 'N/A'}</span></div>
                <div className="detail-item"><label>City</label><span>{selectedRegistration.city || 'N/A'}</span></div>
                <div className="detail-item"><label>State</label><span>{selectedRegistration.state || 'N/A'}</span></div>
                <div className="detail-item" style={{ gridColumn: 'span 2' }}><label>Address</label><span>{selectedRegistration.address || 'N/A'}</span></div>
                <div className="detail-item"><label>Parent Name</label><span>{selectedRegistration.parent_name || 'N/A'}</span></div>
                <div className="detail-item"><label>Parent Phone</label><span>{selectedRegistration.parent_phone || 'N/A'}</span></div>
                <div className="detail-item"><label>Class</label><span>{selectedRegistration.class_tuition_name || 'N/A'}</span></div>
                <div className="detail-item"><label>Course</label><span>{selectedRegistration.course || 'N/A'}</span></div>
                <div className="detail-item" style={{ gridColumn: 'span 2' }}><label>Registration Date &amp; Time</label><span>{formatDate(selectedRegistration.app_registration_date || selectedRegistration.user?.created_at)}</span></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-success" onClick={() => { handleApprove(selectedRegistration.id); setShowModal(false); }}>Approve</button>
              <button className="btn btn-danger" onClick={() => { handleReject(selectedRegistration.id); setShowModal(false); }}>Reject</button>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default RegistrationApprovalsPage;
